import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Prompt } from "react-router";
import { Button, Card, Table } from "antd";
import { map, noop } from "lodash";
import { Modal, message } from "components";
import { LEAVE_CONFIRMATION } from "constants/common";
import { AiccTestAccount } from "types/model/aiccTestAccount";
import * as aiccTestAccountAPI from "services/aiccTestAccount";
import AddTestAccountModal from "./AddTestAccountModal";
import getColumns from "./columnConfig";
import styles from "./TestAccount.module.scss";

const DELETE_TEST_ACCOUNT_MESSAGE =
  "Are you sure you want to delete this test account?\nThis action is a soft delete—all associated data and historical " +
  "records will be safely preserved in the database, but the test account will be removed from this list.";

function TestAccount() {
  const history = useHistory();
  const [testAccounts, setTestAccounts] = useState<AiccTestAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string>();
  const [quotaDraft, setQuotaDraft] = useState<number>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadTestAccounts = useCallback(() => {
    setIsLoading(true);
    return aiccTestAccountAPI
      .fetchAiccTestAccounts()
      .then((accounts) => setTestAccounts(accounts))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadTestAccounts();
  }, [loadTestAccounts]);

  const handleEditQuota = useCallback((account: AiccTestAccount) => {
    setEditingCustomerId(account.customerId);
    setQuotaDraft(account.messageQuota);
  }, []);

  const handleCancelQuota = useCallback(() => {
    setEditingCustomerId(undefined);
    setQuotaDraft(undefined);
  }, []);

  const handleSaveQuota = useCallback(
    (account: AiccTestAccount) => {
      if (!quotaDraft) {
        message.error(new Error("Please enter a message quota."));
        return;
      }
      setIsSubmitting(true);
      aiccTestAccountAPI
        .updateAiccTestAccountQuota(account.customerId, quotaDraft)
        .then(() => {
          message.success(`Updated the message quota of ${account.customerEmail} successfully.`);
          handleCancelQuota();
          return loadTestAccounts();
        })
        .finally(() => setIsSubmitting(false));
    },
    [handleCancelQuota, loadTestAccounts, quotaDraft]
  );

  const handleToggleAccess = useCallback(
    (account: AiccTestAccount, hasAccess: boolean) => {
      setIsSubmitting(true);
      aiccTestAccountAPI
        .updateAiccTestAccountAccess(account.customerId, hasAccess)
        .then(() => {
          message.success(`Preview access of ${account.customerEmail} is ${hasAccess ? "ON" : "OFF"}.`);
          return loadTestAccounts();
        })
        .finally(() => setIsSubmitting(false));
    },
    [loadTestAccounts]
  );

  const handleDelete = useCallback(
    (account: AiccTestAccount) => {
      Modal.confirm({
        title: "Confirm",
        content: <div className={styles.deleteMessage}>{DELETE_TEST_ACCOUNT_MESSAGE}</div>,
        okText: "Confirm Delete",
        onOk: () =>
          aiccTestAccountAPI.deleteAiccTestAccount(account.customerId).then(() => {
            message.success(`Deleted ${account.customerEmail} successfully.`);
            // Not returned: antd holds the dialog in its loading state until onOk's promise
            // settles, so awaiting the refresh kept the modal open behind its own success toast.
            loadTestAccounts().catch(noop);
          }),
      });
    },
    [loadTestAccounts]
  );

  const handleViewSubUser = useCallback((subUserId: string) => history.push(`/aicc/customer/subusers/${subUserId}`), [
    history,
  ]);

  const columns = useMemo(
    () =>
      getColumns({
        editingCustomerId,
        quotaDraft,
        onQuotaDraftChange: setQuotaDraft,
        onEditQuota: handleEditQuota,
        onSaveQuota: handleSaveQuota,
        onCancelQuota: handleCancelQuota,
        onToggleAccess: handleToggleAccess,
        onDelete: handleDelete,
        onViewSubUser: handleViewSubUser,
      }),
    [
      editingCustomerId,
      quotaDraft,
      handleEditQuota,
      handleSaveQuota,
      handleCancelQuota,
      handleToggleAccess,
      handleDelete,
      handleViewSubUser,
    ]
  );

  return (
    <Card
      title="Test Account"
      className="page-container"
      extra={
        <Button type="primary" shape="round" onClick={() => setIsAddModalVisible(true)}>
          + Add Test Account
        </Button>
      }
    >
      <Prompt when={!!editingCustomerId || isSubmitting} message={LEAVE_CONFIRMATION} />
      <Table<AiccTestAccount>
        scroll={{ x: "100%" }}
        loading={isLoading || isSubmitting}
        dataSource={testAccounts}
        columns={columns}
        rowKey={(record) => record.customerId}
        pagination={false}
      />
      <AddTestAccountModal
        visible={isAddModalVisible}
        addedEmails={map(testAccounts, "customerEmail")}
        onCancel={() => setIsAddModalVisible(false)}
        onAdded={setTestAccounts}
      />
    </Card>
  );
}

export default TestAccount;
